import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface User {
  id: string;
  user_id?: string; // Handle database format where user_id is returned instead of id
  email: string;
  name?: string;
  avatar_url?: string;
  preferred_language?: string;
  created_at: string;
  last_login: string;
}

export interface AuthState {
  user: User | null;
  session: any | null; // Supabase session type
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: any) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearAuth: () => void;
  updateUser: (updates: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,

      setUser: (user) => {
        // Validate user data before setting - handle both 'id' and 'user_id' fields
        const userId = user?.id || user?.user_id;
        const validUser = user && userId && userId !== 'undefined' && typeof userId === 'string' 
          ? { ...user, id: userId } // Normalize to always have 'id' field
          : null;
        
        set({ 
          user: validUser, 
          isAuthenticated: !!validUser,
          error: null 
        });
        
        // Log user state changes for debugging
        if (validUser) {
          console.log('✅ Auth: User set', { id: validUser.id, email: validUser.email });
        } else if (user) {
          console.warn('❌ Auth: Invalid user data rejected', user);
        } else {
          console.log('🔄 Auth: User cleared');
        }
      },

      setSession: (session) => {
        const current = get();
        // Deduplicate on access_token only when both sides have a token.
        // If either side is null we always apply the update so that
        // isAuthenticated is kept in sync with the real session state.
        const bothHaveToken = current.session?.access_token && session?.access_token;
        if (bothHaveToken && current.session?.access_token === session?.access_token) {
          return; // Same token, skip update
        }
        set({
          session,
          isAuthenticated: !!session,
          error: null
        });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },

      clearAuth: () => {
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
      },

      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...updates }
          });
        }
      }
    }),
    {
      name: 'converlang-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist minimal user data for session validation
      // Never persist session tokens for security
      partialize: (state) => ({
        // Only persist user ID for session validation
        // Force fresh auth validation on app restart
        user: state.user?.id ? { id: state.user.id } : null
      }),
      
      // Force fresh session validation on rehydration
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn('Auth store rehydration error:', error);
          // Clear any corrupted state
          if (state) {
            state.user = null;
            state.session = null;
            state.isAuthenticated = false;
            state.isLoading = true;
          }
          return;
        }
        
        if (state) {
          // Force loading state to trigger fresh session validation
          state.isLoading = true;
          state.isAuthenticated = false; // Will be set to true only after fresh validation
          state.session = null; // Never restore session from storage
          
          // If we have a persisted user ID, we need to validate it
          if (state.user?.id) {
            console.log('Persisted user ID found, will validate against fresh session');
          }
        }
      }
    }
  )
);